import * as _ from 'lodash-es';
import React from 'react';
import * as styles from './TopicBreadcrumbs.module.scss';
import type { BreadcrumbsProps } from '../components/Breadcrumbs';
import Breadcrumbs from '../components/Breadcrumbs';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import topicPath, { publicTopicPath } from '../utils/url/topicPath';
import useIsPublicView from '../utils/useIsPublicView';

const TOPICS_FRAGMENT = gql(`
  fragment TopicBreadcrumbs_topics on TopicFields {
    name
    ...topicPath_topic
  }
`);

interface TopicBreadcrumbsProps<
  Topics extends DocumentType<typeof TOPICS_FRAGMENT>[],
> {
  link?: boolean;
  names?: React.ReactNode[];
  showRoot?: boolean;
  size?: BreadcrumbsProps['size'];
  tab?: 'subtopics';
  topics: Topics;
  truncate?: BreadcrumbsProps['truncate'];
  innerRef?: React.Ref<HTMLOListElement>;
  maximumAllowedSpace?: number;
  showLastCaret?: boolean;
  renderName?: (
    name: React.ReactNode,
    topic: Topics[number],
  ) => React.ReactNode;
}

const TopicBreadcrumbs = <
  Topics extends DocumentType<typeof TOPICS_FRAGMENT>[],
>({
  link = false,
  names = [],
  showRoot = false,
  size,
  tab = undefined,
  topics,
  truncate,
  innerRef,
  maximumAllowedSpace,
  showLastCaret,
  renderName,
}: TopicBreadcrumbsProps<Topics>) => {
  const isPublicView = useIsPublicView();
  const items = _.compact(
    _.zip(topics, names)
      .toReversed()
      .map(([topic, name]) => {
        if (!topic) return null;

        const finalName = name ?? topic.name;
        const item: BreadcrumbsProps['items'][number] = {
          name: renderName ? renderName(finalName, topic) : finalName,
        };

        if (link) {
          item.to = isPublicView
            ? publicTopicPath(topic)
            : topicPath(topic, tab);
        }

        return item;
      }),
  );

  if (showRoot) {
    items.unshift({ name: 'Content Map', to: link ? '/topics' : undefined });
  }

  return (
    <div className={styles.container}>
      <Breadcrumbs
        ref={innerRef}
        items={items}
        size={size}
        truncate={truncate}
        maximumAllowedSpace={maximumAllowedSpace}
        showLastCaret={showLastCaret}
      />
    </div>
  );
};

export default TopicBreadcrumbs;
